import { Box } from "@mui/material";
import Image from "next/image";
import { useRouter } from "next/router";

import ProfileIcon from "../../assets/Icons/ProfileIcon";
import User from "../../interfaces/User";
import { theme } from "../../mui-customs/Theme";
import Button from "../Button";
import LikeAndUnLikeChef from "../LikeAndUnLikeChef";
import Typography from "../Typography";
import HourlyRateDisplay from "./HourlyRateDisplay";
import ProfileTag from "./ProfileTag";
import RatingDisplay from "./RatingDisplay";

interface ChefProfileCardProps {
  chef: User;
}

const ChefProfileCard = ({ chef }: ChefProfileCardProps) => {
  const router = useRouter();

  return (
    <Box
      sx={{
        py: 3,
        boxSizing: "border-box",
        width: "100%",
        gap: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
      }}
      onClick={() => {
        router.push(`/profile-public/${chef.uid}`);
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxHeight: "300px",
          borderRadius: "6px",
          aspectRatio: "327 / 222",
          position: "relative",
          overflow: "hidden",
          backgroundColor: theme.whiteBg.WBG3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {chef.profileImage ? (
          <Image
            src={chef.profileImage}
            alt={`${chef.firstName} ${chef.lastName} profile picture`}
            layout="fill"
            objectFit="cover"
            priority={false}
            loading="lazy"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        ) : (
          <ProfileIcon />
        )}
      </Box>
      <Box
        sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="title6" sx={{ color: theme.black[800] }}>
            {`${chef.firstName} ${chef.lastName && chef.lastName[0]}.`}
          </Typography>
          <RatingDisplay value={chef.rating} />
        </Box>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              // gap: 1,
              flex: 1,
            }}
          >
            {chef.bio && (
              <Box
                sx={{
                  position: "relative",
                  pr: 3,
                  height: "21px",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body3"
                    sx={{
                      color: theme.grey2[400],
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {chef.bio}
                  </Typography>
                </Box>
              </Box>
            )}
            {chef.address?.locality && chef.address?.state && (
              <Box
                sx={{
                  position: "relative",
                  pr: 3,
                  height: "21px",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body3"
                    sx={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      color: theme.blue[400],
                    }}
                  >
                    {`${chef.address?.locality}, ${chef.address?.state}`}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          <>
            {chef.hourlyRate && <HourlyRateDisplay value={chef.hourlyRate} />}
          </>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {chef.foodPreferences &&
          chef.foodPreferences.map((tag, index) => (
            <ProfileTag key={index} text={tag} />
          ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "end",
          flex: 1,
        }}
      >
        <Box
          sx={{ display: "flex", width: "100%", gap: 2, alignItems: "center" }}
        >
          {chef.uid && <LikeAndUnLikeChef chefUid={chef.uid} />}

          <Button
            onClick={() => {
              router.push(`/profile-public/${chef.uid}`);
            }}
            sx={{ flexGrow: 1 }}
          >
            View Profile
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ChefProfileCard;
